import { HTMLAttributes, forwardRef } from 'react'

type CardVariant = 'default' | 'accent' | 'glow'
type CardPadding = 'none' | 'sm' | 'md' | 'lg'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card style variant */
  variant?: CardVariant
  /** Enable hover effects */
  interactive?: boolean
  /** Padding size */
  padding?: CardPadding
  /** @deprecated Use variant="accent" instead */
  hover?: boolean
  /** @deprecated Use variant="accent" instead */
  accent?: boolean
  /** @deprecated Use variant="glow" instead */
  glow?: boolean
}

const variantClasses: Record<CardVariant, string> = {
  default: '',
  accent: 'card-accent',
  glow: 'card-glow',
}

const paddingClasses: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-6',
  lg: 'p-8',
}

/**
 * Card - Container component with consistent styling
 *
 * @example
 * <Card variant="accent" interactive>
 *   <p>Content</p>
 * </Card>
 *
 * @example
 * <Card padding="lg">
 *   <h2>Title</h2>
 *   <p>Description</p>
 * </Card>
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className = '',
      variant = 'default',
      interactive = false,
      padding,
      // Legacy props - maintain backward compatibility
      hover = false,
      accent = false,
      glow = false,
      children,
      ...props
    },
    ref
  ) => {
    // Support legacy boolean props
    const resolvedVariant = glow ? 'glow' : accent ? 'accent' : variant
    const isInteractive = interactive || hover

    const baseStyles = 'card'
    const variantStyles = variantClasses[resolvedVariant]
    const interactiveStyles = isInteractive ? 'card-hover cursor-pointer' : ''
    const paddingStyles = padding ? paddingClasses[padding] : ''

    return (
      <div
        ref={ref}
        className={`${baseStyles} ${variantStyles} ${interactiveStyles} ${paddingStyles} ${className}`.trim()}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

export default Card
